import { Fragment, useEffect, useState, useMemo, CSSProperties, ReactNode, useImperativeHandle, Ref } from 'react';
import ExpandToggle from './ExpandToggle';
import styles from './Table.module.scss';
import SubTitle from '../../../Text/SubTitle/SubTitle';
import Box from '../../../Layout/Box/Box';
import { withStyles } from '../../../../utils';
import MaterialSymbol from '../../MaterialSymbol/MaterialSymbol';
import { Link as RLink, LinkProps } from 'react-router-dom';

const cn = withStyles(styles);

export type ColField<Data> = Extract<keyof Data, ID>;

export type ColConfig<Data, ColID extends string> = BaseColConfig<Data, ColID> &
  (FieldColConfig<Data> | CustomColConfig<Data>);

type Row<Data> = {
  id: ID;
  data: Data;
  subRows?: SubRow<Data>[];
};

type SubRow<Data> = Omit<Row<Data>, 'subData'>;

type BaseColConfig<Data, ColID extends string> = {
  id: ColID;
  style?: CSSProperties;
  visible?: boolean;
  header?: ReactNode;
  /**
   * when defining this callback, clicking on this column header will
   * fire this individual sort method
   */
  onSort?: (params: { order: SortMethod<Data, ColID>['order'] }) => void;
  // setting sortable to true will invoke the table-level onSort
  sortable?: boolean;
  onHeaderClick?: () => void;
  align?: 'left' | 'center' | 'right';
};

type FieldColConfig<Data> = {
  field: ColField<Data>;
  template?: never;
};

type CustomColConfig<Data> = {
  field?: never;
  template: (r: Row<Data>) => React.ReactNode;
};

type RowWithExtra<Data> = {
  rowId: ID;
  data: Data;
  expanded: boolean;
  selected: boolean;
  subRows?: SubRowWithExtra<Data>[];
};

type SubRowWithExtra<Data> = Omit<Row<Data>, 'subRows'> & {
  subRowId: ID;
  selected: boolean;
};

/**
 * This is so that we can enforce the column ids for onSort.
 * That is, the onSort callback function will receive a id that
 * matches the ids defined/provided in the 'cols' prop
 */
type ColumnID<Data, ColID extends string> = ColConfig<Data, ColID>['id'];

type ID = string | number;

type SortMethod<Data, ColID extends string> = {
  id: ColumnID<Data, ColID>;
  order: 'asc' | 'desc' | 'none';
};

export type TableHandle = {
  resetSort: () => void;
};

type RowPreset = 'error';

// so you don't have to explicitly assign types in the generics
export function createTableProps<Data, ColID extends string>(
  props: Omit<TableProps<Data, ColID>, 'rows' | 'columns'> & {
    rows: Row<Data>[];
    columns: ColConfig<Data, ColID>[];
  },
): TableProps<Data, ColID> {
  return props;
}

export type TableProps<Data, ColID extends string> = {
  id?: string;
  title?: string;
  rows: Row<Data>[];
  subRowEmptyMessage?: string;
  hasSubRows?: boolean;
  rowPresets?: (row: Row<Data>) => RowPreset[];
  columns: ColConfig<Data, ColID>[];
  selectable?: boolean;
  onSelect?: (rows: Row<Data>[]) => void;
  onSort?: (params: SortMethod<Data, ColID>) => void;
  renderTopControls?: (params: {
    allExpanded: boolean;
    toggleExpandAll: () => void;
    selectedRows: Row<Data>[];
    allSelected: boolean;
    toggleSelectAll: () => void;
  }) => React.ReactNode;
  className?: string;
  zebra?: boolean;
  preset?: 'record' | 'pair';
  style?: {
    '--headerBackground'?: string;
    '--headerColor'?: string;
    '--headerBorderRadius'?: string;
    '--rowBorderRadius'?: string;
    '--thBorderBottom'?: string;
  };
};

function Table<Data, ColID extends string>({
  id,
  title,
  rows,
  hasSubRows = false,
  rowPresets = () => [],
  subRowEmptyMessage,
  columns,
  selectable = false,
  onSelect = () => {},
  onSort = () => {},
  renderTopControls,
  className,
  zebra = false,
  preset = 'record',
  style = {
    '--headerBackground': 'var(--secondaryColor)',
    '--headerColor': 'var(--secondaryTextColor)',
    '--headerBorderRadius': '14px',
    '--rowBorderRadius': '14px',
    '--thBorderBottom': 'none',
  },
  ref,
}: TableProps<Data, ColID> & { ref?: Ref<TableHandle> }) {
  const [data, setData] = useState<RowWithExtra<Data>[]>([]);
  const [sortMethod, setSortMethod] = useState<SortMethod<Data, ColID> | null>(null);

  useImperativeHandle(ref, () => ({
    resetSort: () => setSortMethod(null),
  }));
  const allExpanded = useMemo(() => data.every((r) => r.expanded), [data]);
  const allSelected = useMemo(() => data.length > 0 && data.every((r) => r.selected), [data]);

  /**
   * TODO: handle subRows:
   * - if parent is selected and all subRows selected
   * - if parent is selected and some subRows selected
   * - if parent is NOT selected and some/all subRows selected
   */
  const selectedRows = useMemo(
    () =>
      data
        .filter((r) => r.selected)
        .map((r) => ({
          id: r.rowId,
          data: r.data,
          subRows: r.subRows?.map((sr) => ({
            id: sr.subRowId,
            data: sr.data,
          })),
        })),
    [data],
  );

  const maxColSpan = useMemo(() => {
    let count = columns.length;

    if (selectable) count += 1;
    if (hasSubRows) count += 1;

    return count;
  }, [columns]);

  useEffect(() => {
    setData((prev) =>
      rows.map((r) => {
        const existingRow = prev.find(({ rowId }) => rowId === r.id);

        return {
          ...r,
          rowId: r.id,
          expanded: existingRow ? existingRow.expanded : true,
          selected: existingRow ? existingRow.selected : false,
          subRows: r.subRows?.map((sr) => {
            const qwer = existingRow?.subRows?.find(({ subRowId }) => sr.id === subRowId);

            return {
              ...sr,
              subRowId: sr.id,
              selected: qwer ? qwer.selected : false,
            };
          }),
        };
      }),
    );
  }, [rows]);

  useEffect(() => {
    onSelect(selectedRows);
  }, []);

  function handleExpandClick(row: RowWithExtra<Data>) {
    setData((prev) =>
      prev.map((r) => ({
        ...r,
        expanded: r.rowId === row.rowId ? !row.expanded : r.expanded,
      })),
    );
  }

  function handleSelect(selectedId: ID) {
    setData((prev) =>
      prev.map((r) => ({
        ...r,
        selected: r.rowId === selectedId ? !r.selected : r.selected,
        subRows: r.subRows?.map((sr) => ({
          ...sr,
          selected: sr.id === selectedId ? !sr.selected : sr.selected,
        })),
      })),
    );
  }

  const toggleExpandAll = () => {
    setData((prev) =>
      prev.map((r) => ({
        ...r,
        expanded: !allExpanded,
      })),
    );
  };

  function toggleSelectAll() {
    const newSelected = data.map((r) => ({
      ...r,
      id: r.rowId,
      selected: !allSelected,
      subRows: r.subRows?.map((sr) => ({
        ...sr,
        selected: !allSelected,
      })),
    }));

    setData(newSelected);
    onSelect(newSelected);
  }

  return (
    <>
      {title ? (
        <div className={cn('title')}>
          <SubTitle subTitle={title} variant="small" />
        </div>
      ) : (
        <></>
      )}
      {renderTopControls &&
        renderTopControls({
          allExpanded,
          toggleExpandAll,
          selectedRows,
          allSelected,
          toggleSelectAll,
        })}

      <table
        id={id}
        style={style}
        className={cn('table', className, 'preset', preset, zebra && 'zebra')}
        data-testid={`table-${id}`}
      >
        {columns.some((c) => !!c.header) && (
          <thead data-testid={`header-${id}`} className={cn('header')}>
            <tr>
              {selectable && (
                <th className={cn('toggle')}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
              )}

              {hasSubRows && (
                <th className={cn('toggle')}>
                  <ExpandToggle expanded={allExpanded} onClick={toggleExpandAll} />
                </th>
              )}

              {columns.map((c) => {
                if (c.visible !== undefined && !c.visible) return null;
                const onSortIndividual = c.onSort;
                const onSortGlobal = c.sortable;
                const sortable = onSortIndividual || onSortGlobal;

                return (
                  <th
                    key={`${c.id}-header`}
                    role="button"
                    style={{
                      cursor: sortable || c.onHeaderClick ? 'pointer' : 'default ',
                    }}
                    onClick={() => {
                      c.onHeaderClick && c.onHeaderClick();

                      if (sortable) {
                        const nextOrder = getNextSortOrder();

                        onSortIndividual &&
                          onSortIndividual({
                            order: nextOrder,
                          });

                        setSortMethod(nextOrder === 'none' ? null : { id: c.id, order: nextOrder });

                        onSort({
                          id: c.id,
                          order: nextOrder,
                        });
                      }

                      function getNextSortOrder(): SortMethod<Data, ColID>['order'] {
                        if (!sortMethod || sortMethod.id !== c.id) return 'asc';
                        if (sortMethod.order === 'asc') return 'desc';
                        if (sortMethod.order === 'desc') return 'none';
                        return 'asc';
                      }
                    }}
                  >
                    <Box flx="row" ai="center" jc={c.align === 'left' ? 'fs' : c.align === 'right' ? 'fe' : 'center'}>
                      <div style={{ width: 'max-content', textAlign: 'right' }}>{c.header}</div>

                      {sortable && (
                        <div style={{ position: 'relative', marginLeft: '10px' }}>
                          {sortMethod?.id === c.id && sortMethod?.order === 'asc' && (
                            <MaterialSymbol icon="arrow_upward" size="24px" />
                          )}
                          {sortMethod?.id === c.id && sortMethod?.order === 'desc' && (
                            <MaterialSymbol icon="arrow_downward" size="24px" />
                          )}
                          {sortMethod?.id !== c.id && <MaterialSymbol icon="swap_vert" size="24px" />}
                        </div>
                      )}
                    </Box>
                  </th>
                );
              })}
            </tr>
          </thead>
        )}

        <tbody data-testid={`body-${id}`}>
          {data.map((row) => (
            <Fragment key={`${row.rowId}_row`}>
              <RowComponent
                rowId={row.rowId}
                columns={columns}
                row={{ id: row.rowId, ...row }}
                rowPresets={rowPresets({ id: row.rowId, ...row })}
                expanded={row.expanded}
                onExpandClick={() => handleExpandClick(row)}
                expandable={hasSubRows}
                hasSubRowData={Boolean(row.subRows && row.subRows.length > 0)}
                selectable={selectable}
                selected={row.selected}
                onSelect={() => handleSelect(row.rowId)}
              />

              {(function renderChildData() {
                if (!hasSubRows || !row.expanded) return null;

                const childRows =
                  row.subRows?.map((subRow, idx, arr) => (
                    <SubRowComponent
                      key={`${row.rowId.toString()}__${subRow.id}`}
                      rowId={subRow.id}
                      rowPresets={rowPresets({ id: row.rowId, ...row })}
                      columns={columns}
                      row={subRow}
                      selectable={selectable}
                      // selected={subRow.selected}
                      // onSelect={() => handleSelect(subRow.id)}
                      first={idx === 0}
                      last={idx === arr.length - 1}
                    />
                  )) || [];

                return childRows.length > 0 ? (
                  childRows
                ) : subRowEmptyMessage ? (
                  <tr className={cn('row', 'subRow', 'rowGroupFirst', 'rowGroupLast', 'childDataEmptyMessage')}>
                    <td colSpan={maxColSpan} className={cn('emptySubrow')}>
                      {subRowEmptyMessage}
                    </td>
                  </tr>
                ) : null;
              })()}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Link({ children, to }: { children: ReactNode; to: LinkProps['to'] }) {
  return (
    <RLink className={styles.tableLink} to={to}>
      {children}
    </RLink>
  );
}

Table.Link = Link;

export default Table;

type RowProps<Data, ColID extends string> = {
  row: Row<Data>;
  rowPresets: RowPreset[];
  rowId: ID;
  columns: ColConfig<Data, ColID>[];
  expanded?: boolean;
  expandable: boolean;
  selectable: boolean;
  selected: boolean;
  hasSubRowData: boolean;
  onExpandClick?: () => void;
  onSelect?: () => void;
};

function RowComponent<Data, ColID extends string>({
  rowId,
  columns,
  row,
  rowPresets,
  expanded = false,
  expandable,
  selectable,
  selected,
  hasSubRowData,
  onExpandClick = () => {},
  onSelect = () => {},
}: RowProps<Data, ColID>) {
  return (
    <tr className={cn('row', 'rowPreset', 'rowGroupFirst', 'rowGroupLast', expanded && 'expanded', ...rowPresets)}>
      {selectable && (
        <td className={cn('toggle')}>
          <input type="checkbox" checked={selected} onChange={onSelect} />
        </td>
      )}

      {expandable && <td>{hasSubRowData && <ExpandToggle expanded={expanded} onClick={onExpandClick} />}</td>}

      <Cells row={row} columns={columns} rowId={rowId} />
    </tr>
  );
}

type SubRowProps<Data, ColID extends string> = {
  row: Row<Data>;
  rowPresets: RowPreset[];
  rowId: ID;
  columns: ColConfig<Data, ColID>[];
  selectable: boolean;
  // selected: boolean;
  // onSelect?: () => void;
  first: boolean;
  last: boolean;
};

function SubRowComponent<Data, ColID extends string>({
  rowId,
  columns,
  row,
  rowPresets,
  selectable,
  // selected,
  // onSelect = () => {},
  first,
  last,
}: SubRowProps<Data, ColID>) {
  // Potentially assign both classes if there is only one subRow.
  // That is, the single sub row is both the first AND last item.
  const rowGroupFirst = first ? 'rowGroupFirst' : '';
  const rowGroupLast = last ? 'rowGroupLast' : '';

  return (
    <tr className={cn('row', 'subRow', rowGroupFirst, rowGroupLast, 'rowPreset', ...rowPresets)}>
      {selectable && (
        <td>
          {/* select cell; see TODO add selectedRows definition */}
          {/* <input type="checkbox" checked={selected} onChange={onSelect} /> */}
        </td>
      )}

      <td>{/* expand toggle cell */}</td>

      <Cells row={row} columns={columns} rowId={rowId} />
    </tr>
  );
}

function Cells<Data, ColID extends string>({
  row,
  columns,
  rowId,
}: {
  row: Row<Data>;
  columns: ColConfig<Data, ColID>[];
  rowId: ID;
}) {
  return (
    <>
      {columns.map((c) => {
        if (c.visible !== undefined && !c.visible) return null;

        return (
          <td key={`${c.id}_${rowId}`}>
            <div style={c.style} className={cn('cell', 'align', c.align)}>
              {getNode()}
            </div>
          </td>
        );

        function getNode(): React.ReactNode {
          if (isCustomColConfig(c)) {
            return c.template(row);
          }

          if (isFieldColConfig(c)) {
            const node = row.data[c.field];

            if (typeof node === 'string' || typeof node === 'number') {
              return node;
            }

            if (typeof node === 'boolean') {
              return node ? 'true' : 'false';
            }
          }

          return '';
        }
      })}
    </>
  );
}

function isFieldColConfig<Data>(config: FieldColConfig<Data> | CustomColConfig<Data>): config is FieldColConfig<Data> {
  return 'field' in config;
}

function isCustomColConfig<Data>(
  config: FieldColConfig<Data> | CustomColConfig<Data>,
): config is CustomColConfig<Data> {
  return 'template' in config;
}
